// =============================================================================
// RequestDetailPanels.jsx
// =============================================================================
// Medium-sized display panels used inside the request detail view. Pulled out
// of RequestDetail.jsx alongside the smaller atoms (RequestDetailAtoms.jsx)
// to keep that file focused on orchestration.
//
// Exports:
//   DetailSection     — collapsible item-section card (fees / uniforms / etc.)
//   ShoesDetailPanel  — shoes-specific read-only panel for one child
//   StaffFilesPanel   — info pane pointing staff at the Files & Invoices tab
//   ShelterInfoBox    — sidebar with the shelter's staff-only notes + no-approvals flag
// =============================================================================
import React, { useState } from "react";
import { isResponderJob } from "../../app/access";
import { getShelterNotes, upsertShelterNotes } from "../../data/shelterNotes";
import { setNavGuard } from "../../services/navGuard";
import { logAudit } from "../../utils/auditLog";
import { C } from "../../utils/constants";
import { DEFAULT_STANDARD_COSTS, isPrimary } from "../../utils/formConstants";
import { money } from "../../utils/helpers";
import { HelpButton } from "../form/ChildSections.jsx";
import { ChildPanel, RODivider, ROField, ROSubtotal } from "./RequestDetailAtoms.jsx";

// ─────────────────────────────────────────────────────────────────────────────
// DetailSection — one item section in the read-only display (fees / uniforms / etc.)
// ─────────────────────────────────────────────────────────────────────────────
// Renders: section header with icon + title + optional badge + total, plus a
//          collapse toggle. Body content is whatever children the caller
//          passes (typically ROField rows or a ShoesDetailPanel).
// Props (many): icon, title, colour, badge, totalLine, children, forceOpen,
//               onToggle, helpKey, user
export function DetailSection({
  icon,
  title,
  colour,
  badge,
  totalLine,
  children,
  forceOpen,
  onToggle,
  helpKey,
  user,
}) {
  const [open, setOpen] = useState(true);
  const isOpen = forceOpen !== null && forceOpen !== undefined ? forceOpen : open;
  return (
    <div className="border border-zephyr-border rounded-[10px] mb-[14px] overflow-hidden">
      {/* background is a runtime prop — keep inline for that value only */}
      <div
        style={{ background: colour || "#00695C" }}
        className="text-white py-[11px] px-[18px] flex justify-between items-center select-none"
      >
        <button
          type="button"
          onClick={() => {
            setOpen((o) => !o);
            if (onToggle) onToggle();
          }}
          className="flex items-center gap-[10px] flex-1 bg-transparent border-none text-inherit text-left p-0 cursor-pointer"
          style={{ font: "inherit" }}
        >
          <span className="font-bold text-[15px]">
            {icon} {title}
          </span>
          {badge}
          {totalLine && (
            <span className="bg-white/20 rounded-[6px] py-0.5 px-3 text-[13px] font-bold">
              {totalLine}
            </span>
          )}
        </button>
        <div className="flex items-center gap-3">
          {helpKey && isResponderJob(user) && (
            <HelpButton helpKey={helpKey} user={user} light={true} />
          )}
          <button
            type="button"
            onClick={() => {
              setOpen((o) => !o);
              if (onToggle) onToggle();
            }}
            className="bg-transparent border-none text-inherit text-xs opacity-80 cursor-pointer p-0"
            style={{ font: "inherit" }}
          >
            {isOpen ? "^ collapse" : "v expand"}
          </button>
        </div>
      </div>
      {isOpen && <div className="p-4">{children}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ShoesDetailPanel — shoes-specific read-only panel for one child
// ─────────────────────────────────────────────────────────────────────────────
// Renders: joggers + leather quantities and totals inside a ChildPanel.
// Props: c (child object), i (child index)
export function ShoesDetailPanel({ c, i, standardCosts }) {
  // Caps come from the configured standard costs (admin-editable via portal_config),
  // mirroring ChildShoesSection — keep these in sync so the read-only display can't
  // show a stale cap if an admin changes the limit.
  const costs = standardCosts || DEFAULT_STANDARD_COSTS;
  const jSub = (c.jQty || 0) * (parseFloat(c.jCost) || 0);
  const lSub = (c.lQty || 0) * (parseFloat(c.lCost) || 0);
  const primary = isPrimary(c.yearLevel);
  const jMax = primary ? costs.joggers_primary || 60 : costs.joggers_secondary || 80;
  const lMax = costs.leather_shoes || 100;
  return (
    <ChildPanel index={i} colour="#BF360C">
      <ROField label="Gender" value={c.gender} />
      <ROField label="Year Level" value={c.yearLevel} />
      <ROField label="School Type" value={primary ? "Primary" : "Secondary"} />
      <ROField label="School Name" value={c.schoolName} />
      <RODivider label={`Joggers - max $${jMax}`} />
      <ROField label="Quantity" value={c.jQty || 0} />
      <ROField label="Cost/pair" value={money(c.jCost)} />
      <ROField label="Subtotal" value={money(jSub)} />
      <div />
      {!primary && (
        <>
          <RODivider label={`Leather Shoes - max $${lMax}`} />
          <ROField label="Quantity" value={c.lQty || 0} />
          <ROField label="Cost/pair" value={money(c.lCost)} />
          <ROField label="Subtotal" value={money(lSub)} />
          <div />
        </>
      )}
      <ROSubtotal label={`Child ${i + 1} Shoe Total`} value={money(jSub + lSub)} />
    </ChildPanel>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// StaffFilesPanel — info pane shown when staff edit mode is active
// ─────────────────────────────────────────────────────────────────────────────
// Renders: a small banner reminding staff that uploads happen via the Files
//          & Invoices tab in the edit panel, plus a current-attachment count.
// Props: req (uses req.invoices, req.docs)
export function StaffFilesPanel({ req }) {
  const invCount = (req.invoices || []).filter(Boolean).length;
  const docCount = (req.docs || []).filter(Boolean).length;
  return (
    <div className="border border-zephyr-border rounded-[10px] mb-[14px] overflow-hidden">
      <div
        className="text-white py-[11px] px-[18px] font-bold text-[15px]"
        style={{ background: "#546E7A" }}
      >
        Files and Invoices
      </div>
      <div className="p-4">
        <div className="text-[13px] text-zephyr-muted mb-[10px]">
          Use the <strong>Files &amp; Invoices</strong> tab in the edit panel above to upload
          invoices and supporting documents.
        </div>
        {(invCount > 0 || docCount > 0) && (
          <div className="text-[13px] text-zephyr-teal-dark">
            Currently attached: {invCount} invoice{invCount !== 1 ? "s" : ""}, {docCount} document
            {docCount !== 1 ? "s" : ""}.
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ShelterInfoBox — sidebar panel with staff-only notes for the shelter
// ─────────────────────────────────────────────────────────────────────────────
// Renders: the `shelter_notes` note (read-only until Edit → Save/Cancel,
//          Responder-level) plus a READ-ONLY "no requests to approve" flag —
//          the flag is SET in the DFV Service editor (ShelterDetail), shown here
//          for information only.
// Props:
//   shelterCode - preferred lookup key
//   shelterName - fallback lookup (and label)
//   lockEdits   - when true, the notes Edit button is disabled (locked after a
//                 Stationery/Device approval; a supervisor passes lockEdits=false)
export function ShelterInfoBox({ shelterCode, shelterName, lockEdits = false }) {
  const [notes, setNotes] = React.useState("");
  const [noApprovals, setNoApprovals] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [savedMsg, setSavedMsg] = React.useState("");
  // Notes are read-only until the user clicks Edit (then Save / Cancel), mirroring
  // the DFV Service editor. Responder-level authority — no admin gate (John, Aug 2026).
  const [editing, setEditing] = React.useState(false);
  // The values as last SAVED — the audit compares the flag against this (so it
  // only logs a real flip), and both feed the unsaved-changes guard below.
  const savedNoApprovalsRef = React.useRef(false);
  const savedNotesRef = React.useRef("");
  const dirtyRef = React.useRef(false);
  const lookupKey = shelterCode || shelterName;
  const lookupField = shelterCode ? "shelter_code" : "shelter_name";
  // The "no requests to approve" flag is INFO ONLY on the request screen (shown
  // read-only below). It is SET by management in the DFV Service editor
  // (ShelterDetail → Details tab, behind Edit) — a deliberate management action,
  // not something toggled per-request. (Superseded the 2026-06-12 admin-edit here.)

  React.useEffect(
    function () {
      if (!lookupKey) {
        setLoaded(true);
        return;
      }
      getShelterNotes(lookupField, lookupKey).then(function (row) {
        if (row) {
          setNotes(row.notes || "");
          setNoApprovals(row.no_approvals || false);
          savedNoApprovalsRef.current = row.no_approvals || false;
          savedNotesRef.current = row.notes || "";
        }
        setLoaded(true);
      });
    },
    [lookupKey]
  );

  // Unsaved-changes guard: warn before leaving (header nav / Sign Out via
  // navGuard, and the request-detail Back button which awaits it too) and on
  // browser refresh/close. Dirty = the notes text or the no-approvals flag
  // differs from what was last saved.
  React.useEffect(function () {
    const unregister = setNavGuard(function () {
      return dirtyRef.current ? "Your DFV Service notes changes haven't been saved." : null;
    });
    function onBeforeUnload(e) {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return function () {
      unregister();
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  async function save() {
    if (!lookupKey) {
      setSavedMsg("Error: no DFV Service identifier");
      return;
    }
    setSaving(true);
    const row = {
      shelter_code: shelterCode || null,
      shelter_name: shelterName,
      notes: notes,
      no_approvals: noApprovals,
      updated_at: new Date().toISOString(),
    };
    // Upsert: insert or update based on shelter_code unique constraint
    const result = await upsertShelterNotes(row);
    setSaving(false);
    if (result.error) {
      setSavedMsg(`Save failed: ${result.error.message}`);
    } else {
      setSavedMsg("Saved ✓");
      setEditing(false); // back to the read-only view after a successful save
      // Audit a change to the contentious "no requests to be approved" flag —
      // both when it is TICKED and when it is UNTICKED (transition only). The flag
      // is read-only on this screen, so this only fires if it is ever made editable
      // here again; the live audit path is the DFV Service editor (ShelterDetail).
      if (noApprovals !== savedNoApprovalsRef.current) {
        logAudit("shelter_no_approvals_changed", {
          shelter_code: shelterCode || null,
          shelter_name: shelterName,
          enabled: noApprovals,
        });
      }
      // Update the saved snapshot so the unsaved-changes guard clears.
      savedNoApprovalsRef.current = noApprovals;
      savedNotesRef.current = notes;
    }
    setTimeout(function () {
      setSavedMsg("");
    }, 3000);
  }

  // Cancel an edit: revert the notes to the last saved value and leave edit mode.
  function cancelEdit() {
    setNotes(savedNotesRef.current);
    setEditing(false);
    setSavedMsg("");
  }

  // Recomputed every render; the stable guard/beforeunload handlers read the ref.
  dirtyRef.current =
    loaded && (notes !== savedNotesRef.current || noApprovals !== savedNoApprovalsRef.current);

  if (!loaded) return null;

  return (
    <div className="border border-zephyr-border rounded-[10px] overflow-hidden flex flex-col mb-0">
      <div
        className="text-white px-[18px] font-bold text-sm shrink-0 flex items-center h-12"
        style={{ background: C.blue }}
      >
        DFV Service Notes (Responders Only)
      </div>
      <div className="p-[14px] flex-1 flex flex-col">
        {/* INFO ONLY on the request screen: the "no requests to approve" flag is set by
            management in the DFV Service editor (ShelterDetail → Details, behind Edit).
            Ticked → a red banner telling the responder to escalate; not ticked → a plain
            instruction that the notes below are the service's global notes. */}
        {noApprovals ? (
          <div
            className="rounded-[6px] py-2 px-3 mb-[10px] text-[13px] text-white font-bold"
            style={{ background: "#C62828" }}
          >
            This request must be assigned to a team Co-ordinator for Approval
          </div>
        ) : (
          <p className="m-0 mb-[10px] text-[13px] text-zephyr-text font-bold">
            Notes about this DFV Service as a whole — they show on EVERY request from this service.
            Please keep them concise.
          </p>
        )}
        {/* Responders were writing request-only notes here (David, 2 Nov 2026). Point
            them at the Internal Note tab in View / Add Comments instead. */}
        <p
          data-testid="internal-note-pointer"
          className="m-0 mb-[10px] text-[13px] text-zephyr-text bg-zephyr-gold-light border border-zephyr-gold rounded-[6px] py-2 px-[10px] leading-normal"
        >
          For a note about <b>this request only</b>, use <b>View / Add Comments → Internal Note</b>.
          The DFV Service never sees it.
        </p>
        {editing ? (
          <>
            <textarea
              value={notes}
              onChange={function (e) {
                setNotes(e.target.value);
              }}
              placeholder={`Internal notes about ${shelterName || "this DFV Service"}...`}
              className="w-full h-[130px] overflow-y-auto py-2 px-[10px] rounded-[6px] border border-zephyr-border text-[13px] text-zephyr-text box-border resize-none mb-2"
              style={{ fontFamily: "inherit" }}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={save}
                disabled={saving}
                className="bg-zephyr-teal text-white border-none rounded-[6px] py-[6px] px-4 text-[13px] cursor-pointer font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={cancelEdit}
                disabled={saving}
                className="bg-white text-zephyr-text border border-zephyr-border rounded-[6px] py-[6px] px-4 text-[13px] cursor-pointer font-semibold disabled:opacity-50"
              >
                Cancel
              </button>
              {savedMsg && <span className="text-[13px] text-zephyr-teal">{savedMsg}</span>}
            </div>
          </>
        ) : (
          <>
            <div className="w-full h-[130px] overflow-y-auto py-2 px-[10px] rounded-[6px] border border-zephyr-border text-[13px] text-zephyr-text box-border mb-2 whitespace-pre-wrap bg-zephyr-panel-bg">
              {notes ? notes : <span className="text-zephyr-muted italic">No notes yet.</span>}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={function () {
                  setEditing(true);
                }}
                disabled={lockEdits}
                title={
                  lockEdits
                    ? "Locked — a Stationery or Device item has been approved. A supervisor can still edit."
                    : undefined
                }
                className="bg-zephyr-teal text-white border-none rounded-[6px] py-[6px] px-4 text-[13px] cursor-pointer font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Edit
              </button>
              {lockEdits && (
                <span className="text-[12px] text-zephyr-muted italic">
                  Locked after Stationery/Device approval
                </span>
              )}
              {savedMsg && <span className="text-[13px] text-zephyr-teal">{savedMsg}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
